import posthog from "posthog-js";
import { posthogConfig } from "../config";

class PostHogService {
  constructor() {
    this.initialized = false;
  }

  init() {
    if (this.initialized || !posthogConfig.apiKey) {
      return;
    }

    posthog.init(posthogConfig.apiKey, {
      api_host: posthogConfig.apiHost,
      ...posthogConfig.options
    });

    this.initialized = true;
  }

  // page tracking
  capturePageView(path) {
    if (!this.initialized) return;
    posthog.capture("$pageview", { $current_url: path });
  }

  // user identification
  identify(userId, properties = {}) {
    if (!this.initialized) return;
    posthog.identify(userId, properties);
  }

  // user properties
  setPersonProperties(properties) {
    if (!this.initialized) return;
    posthog.setPersonProperties(properties);
  }

  // custom events
  track(eventName, properties = {}) {
    if (!this.initialized) return;
    posthog.capture(eventName, properties);
  }

  // auth events
  trackSignIn(method = "unknown") {
    this.track("user_signed_in", { method });
  }

  trackSignUp(method = "unknown") {
    this.track("user_signed_up", { method });
  }

  trackSignOut() {
    this.track("user_signed_out");
  }

  // dashboard events
  trackDashboardView(dashboardType) {
    this.track("dashboard_viewed", { dashboard_type: dashboardType });
  }

  trackSettingsView(settingsPage) {
    this.track("settings_viewed", { settings_page: settingsPage });
  }

  // campaign events
  trackCampaignCreated(campaignType, properties = {}) {
    this.track("campaign_created", {
      campaign_type: campaignType,
      ...properties
    });
  }

  trackCampaignViewed(campaignId, properties = {}) {
    this.track("campaign_viewed", { campaign_id: campaignId, ...properties });
  }

  trackCampaignEdited(campaignId, properties = {}) {
    this.track("campaign_edited", { campaign_id: campaignId, ...properties });
  }

  // email events
  trackEmailSent(emailType, properties = {}) {
    this.track("email_sent", { email_type: emailType, ...properties });
  }

  trackEmailOpened(emailId, properties = {}) {
    this.track("email_opened", { email_id: emailId, ...properties });
  }

  // payment events
  trackPaymentInitiated(amount, currency = "USD") {
    this.track("payment_initiated", { amount, currency });
  }

  trackPaymentCompleted(amount, currency = "USD", paymentMethod = "") {
    this.track("payment_completed", {
      amount,
      currency,
      payment_method: paymentMethod
    });
  }

  trackPaymentFailed(amount, currency = "USD", errorReason = "") {
    this.track("payment_failed", {
      amount,
      currency,
      error_reason: errorReason
    });
  }

  // feature usage
  trackFeatureUsed(featureName, properties = {}) {
    this.track("feature_used", { feature_name: featureName, ...properties });
  }

  // integration events
  trackIntegrationConnected(integrationType, properties = {}) {
    this.track("integration_connected", {
      integration_type: integrationType,
      ...properties
    });
  }

  trackIntegrationDisconnected(integrationType, properties = {}) {
    this.track("integration_disconnected", {
      integration_type: integrationType,
      ...properties
    });
  }

  // error tracking
  trackError(error, context = {}) {
    this.track("error_occurred", {
      error_message: error.message,
      error_stack: error.stack,
      ...context
    });
  }

  // reset user data (on logout)
  reset() {
    if (!this.initialized) return;
    posthog.reset();
  }

  // get the posthog instance for advanced usage
  getInstance() {
    return this.initialized ? posthog : null;
  }
}

// create singleton instance
const posthogService = new PostHogService();

export default posthogService;
